import React, { useContext, useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, Typography, Chip, Fab, useTheme, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DataContext from '../../../context/DataProvider/DataContext';
import { AgentInputType } from '../../../types/AgentInputType.types';
import Agentv2 from '../../../types/Agentv2.types';

interface AgentHeaderProps {
  values: Agentv2 | AgentInputType;
  isExistingAgent: boolean;
  dirty: boolean;
  isValid: boolean;
  isSubmitting: boolean;
  isFormWithInitValues: boolean;
  errorCount: number;
  onSave: () => void;
  onReset: () => void;
}

function AgentHeader({
  values,
  isExistingAgent,
  dirty,
  isValid,
  isSubmitting,
  isFormWithInitValues,
  errorCount,
  onSave,
  onReset,
}: AgentHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { brachOffices, status } = useContext(DataContext);

  // Avatar-Refresh: Counter hochzählen wenn sich thumbnail ändert → erzwingt neues Laden
  const { thumbnail } = values as Agentv2;
  const [avatarVersion, setAvatarVersion] = useState(0);
  const prevThumbnail = useRef(thumbnail);
  useEffect(() => {
    if (thumbnail !== prevThumbnail.current) {
      prevThumbnail.current = thumbnail;
      setAvatarVersion((v) => v + 1);
    }
  }, [thumbnail]);

  const branchOffice = brachOffices.find((b) => b.id === values.branch_office_id);
  const statusItem = status.find((s) => s.id === values.status_id);

  const saveDisabled = isExistingAgent ? !isValid || !dirty : isFormWithInitValues || (!isValid && !dirty);

  const resetDisabled = isExistingAgent ? !dirty || isSubmitting : isFormWithInitValues && (!dirty || isSubmitting);

  const startDate = values.start_date
    ? new Date(values.start_date).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 1.5 : 2.5,
          p: isMobile ? 2 : 3,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          mb: 2.5,
          position: 'relative',
          overflow: 'hidden',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark}, transparent)`,
          },
        }}
      >
        <Avatar
          key={avatarVersion}
          src={thumbnail ? `${thumbnail}${thumbnail.includes('?') ? '&' : '?'}v=${avatarVersion}` : undefined}
          sx={{
            width: isMobile ? 48 : 64,
            height: isMobile ? 48 : 64,
            flexShrink: 0,
            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: isMobile ? 16 : 20,
              fontWeight: 800,
              letterSpacing: -0.3,
              mb: 0.5,
              color: theme.palette.text.primary,
            }}
          >
            {values.name || 'Neuer'} {values.surname || 'Berater'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {statusItem && (
              <Chip
                label={statusItem.title}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: 'uppercase',
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                }}
              />
            )}
            {values.agent_id !== 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                }}
              >
                <BadgeOutlinedIcon sx={{ fontSize: 14, opacity: 0.5 }} />#{values.agent_id}
              </Box>
            )}
            {branchOffice && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                }}
              >
                <BusinessOutlinedIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                {branchOffice.title}
              </Box>
            )}
            {startDate && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                }}
              >
                <CalendarTodayOutlinedIcon sx={{ fontSize: 14, opacity: 0.5 }} />
                seit {startDate}
              </Box>
            )}
          </Box>
        </Box>

        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>
            {errorCount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontSize: 12,
                  color: theme.palette.error.main,
                  mr: 1.5,
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 16 }} />
                {errorCount} {errorCount === 1 ? 'Feld erfordert' : 'Felder erfordern'} Aufmerksamkeit
              </Box>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={onReset}
              disabled={resetDisabled}
              sx={{
                borderColor: alpha(theme.palette.grey[500], 0.2),
                color: theme.palette.text.secondary,
                fontWeight: 700,
                fontSize: 13,
                borderRadius: 1.25,
                '&:hover': {
                  bgcolor: alpha(theme.palette.grey[500], 0.08),
                  borderColor: alpha(theme.palette.grey[500], 0.35),
                },
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={onSave}
              disabled={saveDisabled}
              startIcon={
                isSubmitting ? <CircularProgress size={14} color="inherit" /> : <CheckIcon sx={{ fontSize: 16 }} />
              }
              sx={{
                fontWeight: 700,
                fontSize: 13,
                borderRadius: 1.25,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }}
            >
              Speichern
            </Button>
          </Box>
        )}
      </Box>

      {isMobile && (
        <Fab
          color="primary"
          onClick={onSave}
          disabled={saveDisabled}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 100,
            width: 56,
            height: 56,
            borderRadius: 2,
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
          }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : <CheckIcon />}
          {errorCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: -4,
                right: -4,
                minWidth: 20,
                height: 20,
                borderRadius: '10px',
                bgcolor: theme.palette.error.main,
                color: theme.palette.common.white,
                fontSize: 11,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.5,
              }}
            >
              {errorCount}
            </Box>
          )}
        </Fab>
      )}
    </>
  );
}

export default AgentHeader;
